import { ACCOUNT_TYPES } from '@constants'
import { hasHistoryBeforeNoCFO, PermissionSet, useBusinessContext, usePermission } from '@context'
import { IAccount } from '@query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaExpandAlt } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'
import styled from 'styled-components'
import { EditableCell } from './EditableCell'
import { EditableToggle } from './EditableToggle'
import { EditableVATCell } from './EditableVATCell'
import { EditableTranslatedCell } from './EditableTranslatedCell'
import { IUpdateTabularAccountProps } from '@pages/AccountsPage/sections'
import { Button } from '@root/components'

const accountColumnHelper = createColumnHelper<IAccount>()

interface Props {
  accounts: IAccount[]
  headerHeight: number
  rowHeight: number
  updateData: (data: IUpdateTabularAccountProps) => Promise<any>
  toggleVisibility: (accountId: number, isShown: boolean) => Promise<any>
}

export const TabularAccountTable: React.FC<Props> = ({
  accounts,
  updateData,
  toggleVisibility,
  rowHeight,
  headerHeight
}) => {
  const { t } = useTranslation()

  const { slug, data: business } = useBusinessContext()
  const has_history = hasHistoryBeforeNoCFO(business)
  const hasEditPermission = usePermission(PermissionSet.ACCOUNTS_EDIT)

  const defaultColumn = React.useMemo<Partial<ColumnDef<IAccount>>>(
    () => ({
      size: 200,
      minSize: 100
    }),
    []
  )

  const columns = React.useMemo<ColumnDef<IAccount>[]>(() => {
    const columnDefs: ColumnDef<IAccount>[] = [
      accountColumnHelper.accessor('is_shown', {
        header: t('accounts.tabular.cols.toggleShow'),
        cell: info => (
          <EditableToggle
            value={info.getValue<boolean>()}
            row={info.row}
            onToggle={toggleVisibility}
            disabled={!hasEditPermission}
          />
        ),
        size: 70,
        minSize: 70,
        maxSize: 70
      }),
      accountColumnHelper.accessor('number', {
        header: t('accounts.tabular.cols.number'),
        cell: info => (
          <EditableCell
            name="number"
            label={t('accounts.number')}
            info={t('accounts.numberInfo')}
            isNumber={true}
            value={info.getValue()}
            row={info.row}
            formatter={value => <code>{value}</code>}
            updateData={updateData}
            disabled={!hasEditPermission}
          />
        ),
        size: 90,
        minSize: 60,
        maxSize: 90
      }),
      accountColumnHelper.accessor('name_translations', {
        header: t('accounts.tabular.cols.name'),
        cell: info => (
          <EditableTranslatedCell
            name="name_translations"
            label={t('accounts.name')}
            info={t('accounts.nameInfo')}
            value={info.getValue()}
            row={info.row}
            updateData={updateData}
            disabled={!hasEditPermission}
          />
        ),
        size: 320,
        minSize: 250,
        maxSize: 400,
        meta: { flexGrow: 2 }
      }),
      accountColumnHelper.accessor('type', {
        header: t('accounts.tabular.cols.type'),
        cell: info => (
          <EditableCell
            info={t('accounts.accountTypeInfo')}
            label={t('accounts.accountType')}
            name="type"
            value={info.getValue()}
            row={info.row}
            updateData={updateData}
            options={ACCOUNT_TYPES.map(({ value, labelKey }) => ({ value, label: t(labelKey) }))}
            disabled={!hasEditPermission}
          />
        ),
        size: 140,
        minSize: 100,
        maxSize: 140,
        meta: { flexGrow: 1 }
      }),
      accountColumnHelper.display({
        id: 'vat_setting',
        header: t('accounts.tabular.cols.vat'),
        cell: info => (
          <EditableVATCell
            account={info.row.original}
            vatCodeValue={info.row.original.default_vat_code}
            vatRateLabelValue={info.row.original.default_vat_rate_label}
            updateData={updateData}
            disabled={!hasEditPermission}
          />
        ),
        size: 120,
        minSize: 100,
        maxSize: 140,
        meta: { flexGrow: 1 }
      })
    ]

    if (has_history) {
      columnDefs.push(
        accountColumnHelper.accessor('opening_balance', {
          header: t('accounts.tabular.cols.openingBalance'),
          cell: info => (
            <EditableCell
              info={t('accounts.initialBalanceInfo')}
              label={t('accounts.initialBalance')}
              name="opening_balance"
              isNumber={true}
              value={info.getValue()}
              row={info.row}
              updateData={updateData}
              disabled={!hasEditPermission}
            />
          ),
          size: 110,
          minSize: 90,
          maxSize: 120,
          meta: { flexGrow: 1 }
        })
      )
    }

    columnDefs.push(
      accountColumnHelper.display({
        id: 'menu',
        header: '',
        cell: info => (
          <MenuIconWrapper style={{ height: rowHeight }}>
            <Link to={`/${slug}/accounts?id=${info.row.original.id}`}>
              <Button size="sm" icon={<FaExpandAlt />}>
                {t('general.show')}
              </Button>
            </Link>
          </MenuIconWrapper>
        ),
        size: 60,
        minSize: 60,
        maxSize: 60
      })
    )

    return columnDefs
  }, [hasEditPermission, has_history, rowHeight, slug, t, updateData, toggleVisibility])

  const table = useReactTable({
    data: accounts,
    columns,
    defaultColumn,
    getCoreRowModel: getCoreRowModel()
  })

  const gridTemplateColumns = React.useMemo(() => {
    const leafColumns = table.getVisibleLeafColumns()

    if (!leafColumns.length) {
      return '1fr'
    }

    return leafColumns
      .map(column => {
        const meta = column.columnDef.meta as { flexGrow?: number } | undefined
        const minSize = column.columnDef.minSize ?? column.columnDef.size ?? column.getSize()

        if (meta?.flexGrow) {
          return `minmax(${minSize}px, ${meta.flexGrow}fr)`
        }

        const size = column.columnDef.size ?? column.getSize()
        return `${size}px`
      })
      .join(' ')
  }, [table])

  return (
    <StyledTableWrapper>
      <div className="table">
        <div>
          {table.getHeaderGroups().map((headerGroup, i) => (
            <StyledHeaderRow
              key={headerGroup.id ?? i}
              style={{ borderBottom: '1px solid #eee', height: headerHeight }}
              gridTemplateColumns={gridTemplateColumns}
            >
              {headerGroup.headers.map((header, i) => (
                <StyledHeader key={header.id ?? i} className="th">
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </StyledHeader>
              ))}
            </StyledHeaderRow>
          ))}
        </div>

        <div>
          {table.getRowModel().rows.map(row => (
            <StyledRow
              key={row.id}
              style={{ borderBottom: '1px solid #eee', height: rowHeight }}
              gridTemplateColumns={gridTemplateColumns}
            >
              {row.getVisibleCells().map(cell => (
                <StyledCell key={cell.id} className="td">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </StyledCell>
              ))}
            </StyledRow>
          ))}
        </div>
      </div>
    </StyledTableWrapper>
  )
}

const StyledTableWrapper = styled.div`
  padding: 0rem;
  position: relative;
  width: 100%;

  .table {
    background: white;
    min-width: fit-content !important;
    border-radius: ${({ theme }) => theme.borderRadius.md}rem;
    width: 100%;
  }
`

const StyledHeaderRow = styled.div<{ gridTemplateColumns: string }>`
  display: grid;
  grid-template-columns: ${({ gridTemplateColumns }) => gridTemplateColumns};
`

const StyledHeader = styled.div`
  white-space: nowrap;
  text-align: left;
  font-weight: bold;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;
  text-overflow: ellipsis;
`

const StyledCell = styled.div`
  white-space: nowrap;
  text-align: left;
  color: ${({ theme }) => theme.colors.neutralBlack};
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;

  & > * {
    min-width: 0;
  }
`

const StyledRow = styled.div<{ gridTemplateColumns: string }>`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ gridTemplateColumns }) => gridTemplateColumns};
  &:hover {
    .menu-icon {
      opacity: 1;
    }
  }
`

const MenuIconWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing.xxs}rem;
  justify-content: flex-end;
`
